import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from './AuthContext';

interface Org {
  id: string;
  name: string;
  slug: string;
  role: string;
}

interface OrgContextType {
  orgs: Org[];
  currentOrg: Org | null;
  setCurrentOrgId: (id: string) => void;
  createOrg: (name: string) => Promise<Org>;
  loading: boolean;
  error: string | null;
}

const OrgContext = createContext<OrgContextType | undefined>(undefined);

export const ORG_ID_STORAGE_KEY = 'personaBuilderApp.currentOrgId';

export const OrgProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [currentOrgId, setCurrentOrgIdState] = useState<string | null>(
    () => localStorage.getItem(ORG_ID_STORAGE_KEY),
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchOrgs = useCallback(async () => {
    if (!user) {
      setOrgs([]);
      setLoading(false);
      return;
    }

    setError(null);
    const { data, error: queryError } = await supabase
      .from('org_members')
      .select('org_id, role, orgs(id, name, slug)')
      .eq('user_id', user.id);

    if (queryError) {
      console.error('Failed to load orgs:', queryError.message);
      setError(`Failed to load organizations: ${queryError.message}`);
      setOrgs([]);
      setLoading(false);
      return;
    }

    const mapped: Org[] = (data ?? []).map((row: any) => ({
      id: row.orgs.id,
      name: row.orgs.name,
      slug: row.orgs.slug,
      role: row.role,
    }));

    setOrgs(mapped);
    // Drop a stale persisted org id (e.g. the user was removed from that org)
    // so a refresh doesn't restore a selection that no longer resolves.
    setCurrentOrgIdState((prev) => {
      if (prev && !mapped.some((o) => o.id === prev)) {
        localStorage.removeItem(ORG_ID_STORAGE_KEY);
        return null;
      }
      return prev;
    });
    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchOrgs();
  }, [fetchOrgs]);

  // Persist selected org
  const setCurrentOrgId = (id: string) => {
    setCurrentOrgIdState(id);
    localStorage.setItem(ORG_ID_STORAGE_KEY, id);
  };

  const createOrg = async (name: string): Promise<Org> => {
    if (!user) throw new Error('Must be signed in to create an org');

    const slug = name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 60);

    // Use RPC to atomically create org + member + settings (bypasses RLS bootstrap issue)
    const { data, error } = await supabase
      .rpc('create_org', { org_name: name, org_slug: `${slug}-${Date.now()}` });

    if (error) throw new Error(`Failed to create org: ${error.message}`);

    const newOrg: Org = {
      id: data.id,
      name: data.name,
      slug: data.slug,
      role: 'owner',
    };

    setOrgs((prev) => [...prev, newOrg]);
    setCurrentOrgId(newOrg.id);

    return newOrg;
  };

  // Auto-select org if only one exists, or restore previous selection
  const currentOrg =
    orgs.find((o) => o.id === currentOrgId) ?? (orgs.length === 1 ? orgs[0] : null);

  // Keep storage in sync when auto-selected
  useEffect(() => {
    if (currentOrg && currentOrg.id !== currentOrgId) {
      setCurrentOrgId(currentOrg.id);
    }
  }, [currentOrg, currentOrgId]);

  return (
    <OrgContext.Provider value={{ orgs, currentOrg, setCurrentOrgId, createOrg, loading, error }}>
      {children}
    </OrgContext.Provider>
  );
};

export const useOrg = () => {
  const context = useContext(OrgContext);
  if (!context) {
    throw new Error('useOrg must be used within an OrgProvider');
  }
  return context;
};
